import { useCallback, useEffect, useState } from "react";

import { useMsal } from "@azure/msal-react";
import { useDispatch } from "react-redux";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { DocumentCategory } from "@/constants/documents";
import { useExportCase } from "@/hooks/mutations/use-export-case";
import { useUpdateFileClassification } from "@/hooks/mutations/use-files";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";
import { setMessage } from "@/redux/notification";
import { getFileUrl } from "@/utils/file";
import { getFriendlyErrorMessage, notifyError } from "@/utils/notification";

import { DocumentPreviewPanel } from "./DocumentPreviewPanel";
import { DocumentTable } from "./DocumentTable";
import { FileWithMetadata } from "./types";
import { useClassificationEditor } from "./useClassificationEditor";

type ManualClassificationDialogProps = {
  files: FileWithMetadata[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onFileClassified?: (fileId: string) => void;
  caseNumber?: string;
  onToastClose?: () => void;
};

export const ManualClassificationDialog = ({
  files,
  open,
  onOpenChange,
  onFileClassified,
  caseNumber,
  onToastClose,
}: ManualClassificationDialogProps) => {
  const { accounts } = useMsal();
  const updateClassificationMutation = useUpdateFileClassification();
  const exportCase = useExportCase();
  const dispatch = useDispatch();

  const {
    displayFiles,
    modifiedFiles,
    getFileClassification,
    hasOtherLOI,
    setClassification,
    findDisplayFile,
    getPageRange,
  } = useClassificationEditor(files, caseNumber);

  const [selectedFile, setSelectedFile] = useState<FileWithMetadata | null>(
    files.length > 0 ? { ...files[0] } : null
  );
  const [selectedClassification, setSelectedClassification] = useState<string>(
    selectedFile?.classification || "Miscellaneous"
  );

  const [isLoading, setIsLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<"pdf" | "image" | "other" | null>(null);
  const [fileLoadError, setFileLoadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: caseData } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const handleFileSelect = useCallback(
    async (file: FileWithMetadata) => {
      if (!file || !(file.filename || file.name)) {
        console.error("Invalid file data", file);
        return;
      }

      const baseFile = findDisplayFile(file);
      const currentClassification = getFileClassification(baseFile);

      setSelectedFile(baseFile);
      setSelectedClassification(currentClassification);
      setIsLoading(true);
      setFileLoadError(false);

      try {
        const result = await getFileUrl(
          {
            ...baseFile,
            name: baseFile.name || baseFile.filename,
            case: baseFile.case,
            classification: baseFile.classification,
          },
          accounts
        );

        if (result.url) {
          setFileUrl(result.url);
          const mappedType =
            result.type === "pdf"
              ? "pdf"
              : result.type === "image"
                ? "image"
                : "other";
          setFileType(mappedType);
        } else {
          setFileUrl(null);
          setFileType("other");
        }
      } catch (error) {
        console.error("Error fetching file URL:", error);
        const friendly = getFriendlyErrorMessage(error);
        notifyError(error, dispatch);
        setFileLoadError(true);
        setErrorMessage(friendly);
      } finally {
        setIsLoading(false);
      }
    },
    [accounts, findDisplayFile, getFileClassification, dispatch]
  );

  // Load first document preview when dialog opens
  useEffect(() => {
    if (open && displayFiles.length > 0 && !fileUrl) {
      handleFileSelect(displayFiles[0]);
    }
  }, [open, displayFiles, fileUrl, handleFileSelect]);

  useEffect(() => {
    return () => {
      if (fileUrl && fileUrl.startsWith("blob:")) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  const handleClassificationChange = useCallback(
    (file: FileWithMetadata, value: string) => {
      const fileId = file.id || file.fileId;
      if (!fileId) {
        console.error("File ID is missing", file);
        return;
      }

      if (value === DocumentCategory.LOI && hasOtherLOI(fileId)) {
        dispatch(
          setMessage({
            content:
              "Only one LOI is allowed. Please remove the existing LOI before setting another file as LOI.",
            type: "warning",
          })
        );
        return;
      }

      setClassification(file, value);

      if (selectedFile?.filename === file.filename) {
        setSelectedClassification(value);
      }
    },
    [hasOtherLOI, setClassification, selectedFile, dispatch]
  );

  const handleUpdateClassifications = async () => {
    try {
      setErrorMessage(null);
      const filesToUpdate = Array.from(modifiedFiles.values()).filter(
        (file) =>
          file.classification !==
          DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS
      );

      await Promise.all(
        filesToUpdate.map(async (file) => {
          const fileId = file.id || file.fileId;
          if (!fileId) return;

          await updateClassificationMutation.mutateAsync({
            fileId,
            classification: file.classification,
          });

          onFileClassified?.(fileId);
        })
      );

      if (!caseData) return;

      const fileIds = displayFiles
        .map((f) => f.id || f.fileId)
        .filter(Boolean) as string[];

      await exportCase.mutateAsync({ caseId: caseData.id, fileIds });

      onOpenChange?.(false);
      onToastClose?.();
    } catch (error) {
      console.error("Error updating classifications or exporting case:", error);
      const friendly = getFriendlyErrorMessage(error);
      notifyError(error, dispatch);
      setErrorMessage(friendly);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!open && !onOpenChange && (
        <DialogTrigger asChild>
          <Button className="mt-3 w-full">Review Files</Button>
        </DialogTrigger>
      )}
      <DialogContent className="h-[80vh] max-h-[900px] pt-12 sm:max-w-[90vw]">
        <div className="grid grid-cols-1 gap-4 overflow-hidden sm:grid-cols-[2fr_1fr]">
          <Card className="h-full overflow-y-auto p-4">
            <div>
              <h3 className="font-medium">Document Classification</h3>
              <p className="text-sm text-foreground-muted">
                Review and classify your processed documents.
              </p>
            </div>

            <DocumentTable
              files={displayFiles}
              selectedFilename={selectedFile?.filename}
              getClassification={getFileClassification}
              selectedClassification={selectedClassification}
              getPageRange={getPageRange}
              onFileSelect={handleFileSelect}
              onClassificationChange={handleClassificationChange}
            />
          </Card>

          <Card className="grid h-full min-h-0 grid-rows-[1fr_auto] gap-4 overflow-hidden p-4">
            <DocumentPreviewPanel
              selectedFile={selectedFile}
              selectedClassification={selectedClassification}
              pageRange={selectedFile ? getPageRange(selectedFile) : ""}
              fileUrl={fileUrl}
              fileType={fileType}
              isLoading={isLoading}
              fileLoadError={fileLoadError}
              errorMessage={errorMessage}
              isSaving={updateClassificationMutation.isPending}
              onRetry={
                selectedFile ? () => handleFileSelect(selectedFile) : undefined
              }
              onAccept={handleUpdateClassifications}
            />
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
